import React, { useState } from "react"
import { useCombobox } from "downshift"
import { Currency } from "../../util/numbers"
import { RecommendedPayee } from "./types"

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const MINIMUM_DISTRIBUTION_AMOUNT = 1

const inputClass =
  "appearance-none border border-gray-300 rounded-sm w-full text-sm py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
const labelClass = "block text-gray-900 text-xs uppercase font-bold mb-1"

interface ExistingDistribution {
  band_id: number | null
  pending_band_email: string | null
}

interface BandResult {
  id: number
  name: string
  cover_image_url: string | null
}

interface PayeeFormInitialValues {
  bandId: number | null
  name: string
  role: string
  amount: string
}

interface PayeeFormProps {
  remainingAmount: number
  excludeBandIds: number[]
  excludeEmails: string[]
  initialValues?: PayeeFormInitialValues
  submitLabel: string
  onSubmit: (bandId: number | null, email: string | null, role: string | null, amount: number) => Promise<void>
  onCancel?: () => void
}

const PayeeForm: React.FC<PayeeFormProps> = ({
  remainingAmount,
  excludeBandIds,
  excludeEmails,
  initialValues,
  submitLabel,
  onSubmit,
  onCancel,
}) => {
  const [searchResults, setSearchResults] = useState<BandResult[]>([])
  const [inputValue, setInputValue] = useState(initialValues?.name || "")
  const [selectedEntry, setSelectedEntry] = useState<{ id: number | null; name: string } | null>(
    initialValues ? { id: initialValues.bandId, name: initialValues.name } : null
  )
  const [role, setRole] = useState(initialValues?.role || "")
  const [amount, setAmount] = useState(initialValues?.amount || "")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [amountError, setAmountError] = useState<string | null>(null)

  const isEmailInput = EMAIL_REGEX.test(inputValue.trim())

  const searchBands = async (query: string) => {
    if (query.length < 2) {
      setSearchResults([])
      return
    }
    try {
      const response = await fetch(`/api/v1/search/bands/${encodeURIComponent(query)}?exclude_fans=true`)
      const data = await response.json()
      setSearchResults(data.success && data.bands?.length > 0 ? data.bands : [])
    } catch {
      setSearchResults([])
    }
  }

  const { isOpen, getMenuProps, getInputProps, getItemProps, highlightedIndex } = useCombobox({
    items: searchResults,
    inputValue,
    onInputValueChange: ({ inputValue: val = "" }) => {
      setInputValue(val)
      searchBands(val)
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) {
        setSelectedEntry({ id: selectedItem.id, name: selectedItem.name })
        setInputValue(selectedItem.name)
        setSearchResults([])
      }
    },
    itemToString: (item) => item?.name || "",
  })

  const handleAddEmail = () => {
    setSelectedEntry({ id: null, name: inputValue.trim() })
    setSearchResults([])
  }

  const validateAndSubmit = async () => {
    setAmountError(null)
    const amountNum = parseFloat(amount)
    if (isNaN(amountNum) || amountNum < MINIMUM_DISTRIBUTION_AMOUNT) {
      setAmountError(`Enter an amount of at least ${Currency(MINIMUM_DISTRIBUTION_AMOUNT)}`)
      return
    }
    if (amountNum > remainingAmount) {
      setAmountError(`Amount exceeds remaining balance of ${Currency(remainingAmount)}`)
      return
    }
    if (!selectedEntry) {
      setAmountError("Select a payee or enter an email")
      return
    }

    setIsSubmitting(true)
    try {
      const bandId = selectedEntry.id
      const email = bandId ? null : selectedEntry.name
      await onSubmit(bandId, email, role.trim() || null, amountNum)
      if (!initialValues) {
        setSelectedEntry(null)
        setInputValue("")
        setRole("")
        setAmount("")
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <label className={labelClass}>Payee</label>
        <div className="relative">
          {!selectedEntry ? (
            <>
              <input
                {...getInputProps()}
                className={inputClass}
                value={inputValue}
                placeholder="Search by name or enter email"
                autoComplete="off"
              />
              <ul
                {...getMenuProps()}
                className="absolute z-10 w-full bg-white border border-gray-300 shadow-md max-h-48 overflow-y-auto"
              >
                {isOpen &&
                  searchResults.map((band, index) => (
                    <li
                      key={band.id}
                      {...getItemProps({ item: band, index })}
                      className={`px-3 py-2 cursor-pointer text-sm ${
                        highlightedIndex === index ? "bg-gray-100" : ""
                      } ${excludeBandIds.includes(band.id) ? "opacity-40 pointer-events-none" : ""}`}
                    >
                      {band.name}
                      {excludeBandIds.includes(band.id) && (
                        <span className="ml-1 text-xs text-gray-400">(already added)</span>
                      )}
                    </li>
                  ))}
                {isOpen && isEmailInput && (
                  <li
                    className="px-3 py-2 cursor-pointer text-sm bg-blue-50 hover:bg-blue-100"
                    onMouseDown={handleAddEmail}
                  >
                    Add by email: <strong>{inputValue.trim()}</strong>
                    {excludeEmails.includes(inputValue.trim()) && (
                      <span className="ml-1 text-xs text-gray-400">(already added)</span>
                    )}
                  </li>
                )}
              </ul>
            </>
          ) : (
            <div className="flex items-center gap-2 py-2">
              <span className="text-sm font-medium">{selectedEntry.name}</span>
              <button
                onClick={() => { setSelectedEntry(null); setInputValue("") }}
                className="text-xs text-gray-500 hover:text-black underline"
              >
                Change
              </button>
            </div>
          )}
        </div>
      </div>

      <div>
        <label className={labelClass}>Description (optional)</label>
        <input
          className={inputClass}
          value={role}
          onChange={(e) => setRole(e.target.value)}
          placeholder="e.g. Drummer, Sound Engineer"
        />
      </div>

      <div>
        <label className={labelClass}>Amount ($)</label>
        <input
          className={`${inputClass}${amountError ? " border-red-500" : ""}`}
          type="number"
          min={1}
          step={1}
          max={remainingAmount}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <p className={`text-xs mt-1 ${amountError ? "text-red-600" : "text-gray-500"}`}>
          {amountError || `Max: ${Currency(remainingAmount)}`}
        </p>
      </div>

      <div className="flex gap-2">
        <button
          onClick={validateAndSubmit}
          disabled={isSubmitting || !amount}
          className="bg-black text-white text-sm font-bold uppercase px-4 py-2 hover:bg-gray-800 disabled:opacity-50"
        >
          {isSubmitting ? "Saving..." : submitLabel}
        </button>
        {onCancel && (
          <button
            onClick={onCancel}
            className="text-sm text-gray-500 hover:text-black px-2 py-2"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  )
}

interface PayeeSearchProps {
  payoutId: number
  recommendedPayees: RecommendedPayee[]
  existingDistributions: ExistingDistribution[]
  remainingAmount: number
  onAdd: (bandId: number | null, email: string | null, role: string | null, amount: number) => Promise<void>
}

export const PayeeSearch: React.FC<PayeeSearchProps> = ({
  recommendedPayees,
  existingDistributions,
  remainingAmount,
  onAdd,
}) => {
  const [recommendedPrefill, setRecommendedPrefill] = useState<PayeeFormInitialValues | null>(null)

  const existingBandIds = existingDistributions.map((d) => d.band_id).filter((id): id is number => !!id)
  const existingEmails = existingDistributions.map((d) => d.pending_band_email).filter((e): e is string => !!e)

  const filteredRecommended = recommendedPayees.filter((r) => !existingBandIds.includes(r.band_id))

  return (
    <div>
      {filteredRecommended.length > 0 && (
        <div className="mb-3">
          <div className={`${labelClass} mb-1`}>Suggested</div>
          <div className="flex flex-wrap gap-2">
            {filteredRecommended.map((rec) => (
              <button
                key={rec.band_id}
                onClick={() =>
                  setRecommendedPrefill({ bandId: rec.band_id, name: rec.band_name, role: rec.role, amount: "" })
                }
                className="text-xs bg-gray-100 border border-gray-300 rounded px-2 py-1 hover:bg-gray-200"
              >
                {rec.band_name}
                <span className="ml-1 text-gray-400">({rec.role})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <PayeeForm
        key={recommendedPrefill?.bandId ?? "blank"}
        remainingAmount={remainingAmount}
        excludeBandIds={existingBandIds}
        excludeEmails={existingEmails}
        initialValues={recommendedPrefill || undefined}
        submitLabel="Add Distribution"
        onSubmit={async (bandId, email, role, amount) => {
          await onAdd(bandId, email, role, amount)
          setRecommendedPrefill(null)
        }}
        onCancel={recommendedPrefill ? () => setRecommendedPrefill(null) : undefined}
      />
    </div>
  )
}

interface EditDistributionFormProps {
  initialValues: PayeeFormInitialValues
  remainingAmount: number
  excludeBandIds: number[]
  excludeEmails: string[]
  onSave: (bandId: number | null, email: string | null, role: string | null, amount: number) => Promise<void>
  onCancel: () => void
}

export const EditDistributionForm: React.FC<EditDistributionFormProps> = ({
  initialValues,
  remainingAmount,
  excludeBandIds,
  excludeEmails,
  onSave,
  onCancel,
}) => (
  <PayeeForm
    remainingAmount={remainingAmount}
    excludeBandIds={excludeBandIds}
    excludeEmails={excludeEmails}
    initialValues={initialValues}
    submitLabel="Save"
    onSubmit={onSave}
    onCancel={onCancel}
  />
)
